// Le planning des salles (D-123), logique pure : la semaine d'une salle (séances, matchs, réceptions, créneaux de la
// mairie, créneaux libérés, chevauchements), la vérification d'une séance qu'on enregistre (ce qui chevauche, ce qui
// est libre ici ou ailleurs), la ligne de chaque salle, le message à la mairie (vouvoiement : il part de la messagerie
// du bureau, jamais de l'app). Les occurrences viennent de la base (halls_busy), à l'heure du club.

export type HallKind = 'gymnase' | 'reception' | 'autre';
export type HallOwner = 'club' | 'mairie';
export interface Hall {
  id: string; name: string; kind: HallKind; origin?: 'ffbb' | 'club'; owner: HallOwner;
  address?: string | null; town?: string | null; capacity?: number | null; usage?: string | null; inside_id?: string | null;
  lat?: number | null; lng?: number | null; contact_name?: string | null; contact_email?: string | null;
}
export interface Occ {
  event_id: string; team_id: string; kind: 'training' | 'match' | 'other'; title: string; hall_id: string;
  day: string; starts: string; ends: string; cancelled: boolean; one_off: boolean; created_at: string;
  squad_id: string | null; opponent: string | null; competition: string | null;
}
export interface Slot { id?: string; hall_id: string; weekday: number; starts: string; ends: string; team_id: string | null }
export interface Booking { id: string; hall_id: string; day: string; starts: string; ends: string; purpose: Purpose; purpose_text: string | null; people: number | null; who: string | null }
export interface TeamRow { id: string; name: string; category: string | null; squads: { id: string; name: string }[] }
export interface Busy {
  today: string; now?: string; season: string; bureau: boolean; halls: Hall[]; teams: TeamRow[]; occ: Occ[]; slots: Slot[]; bookings: Booking[];
  unplaced?: number;
}
export type Purpose = 'pot' | 'parents' | 'anniversaire' | 'autre';
export type BookingStatus = 'pending' | 'proposed' | 'asked' | 'accepted' | 'refused' | 'cancelled';

export const KIND_LABEL: Record<HallKind, string> = { gymnase: 'Gymnase', reception: 'Salle de réception', autre: 'Autre salle' };
export const PURPOSES: { key: Purpose; label: string }[] = [
  { key: 'pot', label: 'Pot après le match' }, { key: 'parents', label: 'Réunion des parents' },
  { key: 'anniversaire', label: 'Anniversaire du club' }, { key: 'autre', label: 'Autre' },
];
export const purposeLabel = (p: Purpose, text?: string | null) =>
  p === 'autre' ? (text?.trim() || 'Autre') : PURPOSES.find(x => x.key === p)?.label ?? 'Autre';
export const STATUS_LABEL: Record<BookingStatus, string> = {
  pending: 'En attente du bureau', proposed: 'Une autre heure proposée', asked: 'Demandée au gestionnaire',
  accepted: 'Acceptée', refused: 'Refusée', cancelled: 'Annulée',
};
const COMPETITION: Record<string, string> = { championnat: 'championnat', coupe: 'coupe', phase_finale: 'phase finale', brassage: 'brassage', amical: 'amical' };
/** La couleur des réceptions. */
export const REC_COLOR = '#B45309';
/** Une couleur par équipe (ou équipe engagée) : les seniors d'abord, puis des plus grands aux plus petits. */
export const PALETTE = ['#0F4C81', '#3B82C4', '#BE185D', '#0E7490', '#C2410C', '#6D4BC2', '#15803D', '#A16207', '#475569', '#9D174D', '#0369A1', '#B91C1C'];

// --- Dates et heures ------------------------------------------------------------------------------------------------
const pad = (n: number) => String(n).padStart(2, '0');
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseDay = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y!, m! - 1, d!); };
export const addDays = (s: string, n: number) => { const d = parseDay(s); d.setDate(d.getDate() + n); return ymd(d); };
/** 1 = lundi … 7 = dimanche. */
export const isoDay = (s: string) => parseDay(s).getDay() || 7;
export const mondayOf = (s: string) => addDays(s, 1 - isoDay(s));
/** La semaine qu'on ouvre : celle-ci jusqu'au mercredi, la suivante à partir du jeudi (le reste de la semaine est fait). */
export const defaultMonday = (today: string) => (isoDay(today) >= 4 ? addDays(mondayOf(today), 7) : mondayOf(today));
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
export const dayName = (s: string) => DAYS[isoDay(s) - 1]!;
const num = (d: number) => (d === 1 ? '1er' : String(d));
/** « Semaine du 2 au 8 novembre », « Semaine du 30 novembre au 6 décembre ». */
export function weekLabel(monday: string): string {
  const a = parseDay(monday), b = parseDay(addDays(monday, 6));
  return a.getMonth() === b.getMonth() ? `Semaine du ${num(a.getDate())} au ${num(b.getDate())} ${MONTHS[b.getMonth()]}`
    : `Semaine du ${num(a.getDate())} ${MONTHS[a.getMonth()]} au ${num(b.getDate())} ${MONTHS[b.getMonth()]}`;
}
/** « Mardi 3 » */
export const dayLabel = (s: string) => `${cap(dayName(s))} ${num(parseDay(s).getDate())}`;
/** « dimanche 8 novembre » */
export const longDay = (s: string) => `${dayName(s)} ${num(parseDay(s).getDate())} ${MONTHS[parseDay(s).getMonth()]}`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** « 18h00 » d'une date, ou d'une heure « 18:00 ». */
export const hm = (t: Date | string) => (typeof t === 'string' ? t.slice(0, 5).replace(':', 'h') : `${pad(t.getHours())}h${pad(t.getMinutes())}`);
export const range = (a: Date | string, b: Date | string) => `${hm(a)}–${hm(b)}`;
/** Une heure locale d'un jour (« 2026-11-04 », « 18:30 »). */
export const at = (day: string, time: string) => { const d = parseDay(day); const [h, m] = time.split(':').map(Number); d.setHours(h!, m!, 0, 0); return d; };
const overlap = (a0: Date, a1: Date, b0: Date, b1: Date) => a0 < b1 && b0 < a1;
const minutes = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60000);
/** « 30 min », « 1 h », « 1 h 30 » */
export const dur = (min: number) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${pad(min % 60)}` : ''}`);

// --- Équipes : nom court, couleur ----------------------------------------------------------------------------------
/** Ce qui occupe une couleur : une équipe, ou une équipe engagée quand le groupe en a plusieurs (SF1, SF2, D-042). */
export interface Unit { key: string; team_id: string; label: string; color: string }
const rank = (t: TeamRow) => {
  const s = `${t.category ?? ''} ${t.name}`;
  if (/senior/i.test(s)) return 0;
  const u = /U\s?(\d{1,2})/i.exec(s);
  return u ? 100 - Number(u[1]) : 200;
};
/** « U15 garçons » → « U15 G », « U13 filles » → « U13 » s'il n'y a qu'une U13. */
export function shortTeam(name: string, all: string[]): string {
  const cat = /^(U\s?\d{1,2})/i.exec(name)?.[1];
  if (cat && all.filter(n => n.toUpperCase().startsWith(cat.toUpperCase())).length === 1) return cat.replace(/\s/, '');
  return name.replace(/\s+filles?\b/i, ' F').replace(/\s+gar[cç]ons?\b/i, ' G').replace(/\s+mixtes?\b/i, ' M');
}
export function unitsOf(teams: TeamRow[]): Unit[] {
  const names = teams.map(t => t.name);
  const sorted = [...teams].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'fr'));
  const out: Unit[] = [];
  for (const t of sorted) {
    if (t.squads.length > 1) for (const q of t.squads) out.push({ key: q.id, team_id: t.id, label: q.name, color: '' });
    else out.push({ key: t.id, team_id: t.id, label: shortTeam(t.name, names), color: '' });
  }
  out.forEach((u, i) => { u.color = PALETTE[i % PALETTE.length]!; });
  return out;
}
const unitOf = (units: Unit[], teamId: string, squadId: string | null) =>
  units.find(u => u.key === squadId) ?? units.find(u => u.key === teamId) ?? units.find(u => u.team_id === teamId) ?? null;

// --- La semaine d'une salle -----------------------------------------------------------------------------------------
export type RowKind = 'occ' | 'free' | 'idle' | 'rec';
export interface Row {
  key: string; kind: RowKind; day: string; start: Date; end: Date; title: string; sub: string; color: string;
  hall_id: string; unit?: string;
  /** Un chevauchement (en rouge, sur la séance la plus récente) : avec qui, combien de minutes. */
  clash?: { with: string; minutes: number };
  /** « Prendre » : le créneau libéré (ou le créneau de la mairie sans équipe), pour qui peut le prendre. */
  take?: { day: string; starts: string; ends: string; teams: string[] };
  /** La fiche (séance, match) quand on la voit. */
  href?: string;
  event_id?: string; team_id?: string; created_at?: string;
}
export interface Viewer { staffTeams: Set<string>; now: Date }
export interface Day { day: string; rows: Row[] }
export interface Week { monday: string; days: Day[]; legend: Unit[]; slotsPerWeek: number }

const teamName = (b: Busy, id: string) => b.teams.find(t => t.id === id)?.name ?? 'Une équipe';
const squadsLine = (b: Busy, teamId: string) => {
  const sq = b.teams.find(t => t.id === teamId)?.squads ?? [];
  return sq.length > 1 ? ` · ${sq.map(q => q.name).join(sq.length === 2 ? ' et ' : ', ')}` : '';
};
const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Le nom d'une occurrence (« Seniors filles », « SF2 – BC Tavernel »). */
export function occTitle(b: Busy, o: Occ): string {
  if (o.kind !== 'match') return teamName(b, o.team_id);
  const t = b.teams.find(x => x.id === o.team_id);
  const unit = (t && t.squads.length > 1 ? t.squads.find(q => q.id === o.squad_id)?.name : null) ?? t?.name ?? 'Match';
  return `${unit} – ${o.opponent ?? o.title}`;
}
function occSub(b: Busy, o: Occ): string {
  if (o.kind === 'match') return `Match${o.competition && COMPETITION[o.competition] ? ` · ${COMPETITION[o.competition]}` : ''}`;
  if (o.one_off) return 'Séance ajoutée';
  return (/^entraînement\b/i.test(o.title) ? 'Entraînement' : o.title) + squadsLine(b, o.team_id);
}

/** La semaine d'une salle : ses séances et ses matchs, les réceptions (les siennes et celles des salles qui s'y
 *  trouvent), ses créneaux de la mairie (libres, ou sans séance), les créneaux libérés, les chevauchements. */
export function buildWeek(b: Busy, hallId: string, monday: string, v: Viewer): Week {
  const units = unitsOf(b.teams);
  const inside = new Set(b.halls.filter(h => h.inside_id === hallId).map(h => h.id));
  const hallName = (id: string) => b.halls.find(h => h.id === id)?.name ?? '';
  const days: Day[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < 7; i++) {
    const day = addDays(monday, i);
    const occ = b.occ.filter(o => o.hall_id === hallId && o.day === day);
    const live = occ.filter(o => !o.cancelled);
    const rows: Row[] = [];
    for (const o of live) {
      const u = unitOf(units, o.team_id, o.squad_id);
      seen.add(u?.key ?? '');
      rows.push({ key: `o:${o.event_id}:${day}`, kind: 'occ', day, start: new Date(o.starts), end: new Date(o.ends), title: occTitle(b, o), sub: occSub(b, o),
        color: u?.color ?? 'var(--border)', hall_id: hallId, unit: u?.key, event_id: o.event_id, team_id: o.team_id, created_at: o.created_at,
        href: v.staffTeams.has(o.team_id) ? `#/e/${o.event_id}/${day}` : undefined });
    }
    // Les créneaux libérés : une séance annulée (pas un match), que rien n'occupe désormais.
    for (const o of occ.filter(x => x.cancelled && x.kind !== 'match')) {
      const s = new Date(o.starts), e = new Date(o.ends);
      if (live.some(x => overlap(s, e, new Date(x.starts), new Date(x.ends)))) continue;
      const teams = [...v.staffTeams].filter(t => t !== o.team_id);
      rows.push({ key: `f:${o.event_id}:${day}`, kind: 'free', day, start: s, end: e, title: 'Libre', sub: `${teamName(b, o.team_id)} · séance annulée`,
        color: 'var(--border)', hall_id: hallId, team_id: o.team_id,
        take: teams.length && s > v.now ? { day, starts: hhmm(s), ends: hhmm(e), teams } : undefined });
    }
    // Les créneaux du club (donnés par le gestionnaire de la salle) qu'aucune séance n'occupe.
    for (const sl of b.slots.filter(x => x.hall_id === hallId && x.weekday === i + 1)) {
      const s = at(day, sl.starts), e = at(day, sl.ends);
      if (occ.some(x => overlap(s, e, new Date(x.starts), new Date(x.ends)))) continue;
      if (!sl.team_id) {
        const teams = [...v.staffTeams];
        // D-135 : il se demande au responsable des salles (Nos créneaux, ce jour-là), il ne se prend plus.
        rows.push({ key: `s:${sl.id ?? `${i}${sl.starts}`}:${day}`, kind: 'free', day, start: s, end: e, title: 'Libre', sub: 'Créneau du club, sans équipe',
          color: 'var(--border)', hall_id: hallId, href: teams.length && s > v.now ? `#/salles/jour/${day}` : undefined });
      } else {
        rows.push({ key: `s:${sl.id ?? `${i}${sl.starts}`}:${day}`, kind: 'idle', day, start: s, end: e, title: teamName(b, sl.team_id),
          sub: 'Créneau du club · rien de prévu', color: unitOf(units, sl.team_id, null)?.color ?? 'var(--border)', hall_id: hallId, team_id: sl.team_id });
      }
    }
    // Les réceptions acceptées : celles de la salle, et celles des salles qui s'y trouvent (le club-house au gymnase).
    for (const bk of b.bookings.filter(x => x.day === day && (x.hall_id === hallId || inside.has(x.hall_id)))) {
      const own = bk.hall_id === hallId;
      const what = purposeLabel(bk.purpose, bk.purpose_text);
      rows.push({ key: `r:${bk.id}`, kind: 'rec', day, start: at(day, bk.starts), end: at(day, bk.ends),
        title: own ? what : hallName(bk.hall_id), hall_id: bk.hall_id, color: REC_COLOR,
        sub: own ? [bk.who ? `demandé par ${bk.who}` : '', bk.people ? `${bk.people} personnes` : ''].filter(Boolean).join(' · ')
          : `${what}${bk.who ? ` · demandé par ${bk.who}` : ''}` });
    }
    markClashes(rows);
    rows.sort((a, c) => a.start.getTime() - c.start.getTime() || a.title.localeCompare(c.title, 'fr'));
    if (rows.length) days.push({ day, rows });
  }
  return { monday, days, legend: units.filter(u => seen.has(u.key)), slotsPerWeek: b.slots.filter(x => x.hall_id === hallId).length };
}

/** Deux occupations de la même salle qui se croisent : la plus récente (créée après) passe en rouge. */
function markClashes(rows: Row[]): void {
  const busy = rows.filter(r => r.kind === 'occ' || r.kind === 'rec');
  for (const a of busy) for (const c of busy) {
    if (a === c || a.hall_id !== c.hall_id || !overlap(a.start, a.end, c.start, c.end)) continue;
    const later = (a.created_at ?? '') > (c.created_at ?? '') || ((a.created_at ?? '') === (c.created_at ?? '') && a.start > c.start);
    if (!later || a.clash) continue;
    const from = a.start > c.start ? a.start : c.start, to = a.end < c.end ? a.end : c.end;
    a.clash = { with: c.title, minutes: minutes(from, to) };
    a.sub = `${a.sub} · chevauche ${c.title}`;
  }
}

/** Les chevauchements d'une salle sur une période (une fois par paire et par jour). */
export function clashCount(b: Busy, hallId: string): number {
  const live = b.occ.filter(o => o.hall_id === hallId && !o.cancelled);
  let n = 0;
  for (let i = 0; i < live.length; i++) for (let j = i + 1; j < live.length; j++) {
    const a = live[i]!, c = live[j]!;
    if (a.day === c.day && a.event_id !== c.event_id && overlap(new Date(a.starts), new Date(a.ends), new Date(c.starts), new Date(c.ends))) n++;
  }
  return n;
}

/** Les créneaux libérés à venir que ces équipes peuvent prendre (la tuile du coach). */
export function freedFor(b: Busy, staffTeams: Set<string>, now: Date): number {
  return b.occ.filter(o => o.cancelled && o.kind !== 'match' && new Date(o.starts) > now && [...staffTeams].some(t => t !== o.team_id)
    && !b.occ.some(x => !x.cancelled && x.hall_id === o.hall_id && x.day === o.day && overlap(new Date(o.starts), new Date(o.ends), new Date(x.starts), new Date(x.ends)))).length;
}

// --- La ligne de chaque salle (Les salles) --------------------------------------------------------------------------
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
/** « 12 rue du Stade · 14 créneaux par semaine · 6 équipes », « Avenue des Écoles · 4 créneaux · U11, U13 »,
 *  « Prépa physique · jeudi 19h », « Au gymnase · 40 personnes · 1 demande à traiter », « Mairie de Rivebelle · à
 *  réserver auprès de la mairie ». `mairie` : le nom du gestionnaire (D-130) ; `via` : « de la mairie », « du collège »
 *  (sinon « du gestionnaire »). `week` : les occurrences des 7 prochains jours (sans créneaux du club notés). */
export function hallLine(b: Busy, h: Hall, opts: { requests?: number; mairie?: string | null; via?: string; week?: Occ[] } = {}): string {
  const names = b.teams.map(t => t.name);
  const parts: string[] = [];
  if (h.kind === 'reception') {
    const inside = b.halls.find(x => x.id === h.inside_id);
    if (h.owner === 'mairie') parts.push(opts.mairie || 'Son gestionnaire', `à réserver auprès ${opts.via ?? 'du gestionnaire'}`);
    else {
      if (inside) parts.push(inside.kind === 'gymnase' ? 'Au gymnase' : `Dans ${lowerFirst(inside.name)}`);
      else if (h.address) parts.push(h.address);
      if (h.capacity) parts.push(plural(h.capacity, 'personne', 'personnes'));
    }
    if (opts.requests) parts.push(`${plural(opts.requests, 'demande', 'demandes')} à traiter`);
    return parts.join(' · ');
  }
  const slots = b.slots.filter(s => s.hall_id === h.id);
  // « U11 » pour U11 mixte 1 et U11 mixte 2 ; « Seniors F » sinon.
  const cat = (id: string) => { const n = teamName(b, id); return /^(U\s?\d{1,2})/i.exec(n)?.[1]?.replace(/\s/, '') ?? shortTeam(n, names); };
  if (h.usage && slots.length <= 1) {
    const s = slots[0];
    return [h.usage, s ? `${DAYS[s.weekday - 1]} ${s.starts.slice(0, 2).replace(/^0/, '')}h${s.starts.slice(3, 5) === '00' ? '' : s.starts.slice(3, 5)}` : '']
      .filter(Boolean).join(' · ');
  }
  if (h.address) parts.push(h.address);
  else if (h.town) parts.push(h.town);
  const order = unitsOf(b.teams).map(u => u.team_id);
  const teamIds = (slots.length ? slots.map(s => s.team_id).filter((x): x is string => !!x) : (opts.week ?? []).filter(o => o.hall_id === h.id && o.kind !== 'match').map(o => o.team_id))
    .sort((a, c) => order.indexOf(a) - order.indexOf(c));
  const count = slots.length || (opts.week ?? []).filter(o => o.hall_id === h.id && o.kind !== 'match').length;
  const labels = [...new Set(teamIds.map(cat))];
  const nTeams = new Set(teamIds).size;
  if (count) parts.push(labels.length > 2 || !labels.length ? `${plural(count, 'créneau', 'créneaux')} par semaine` : plural(count, 'créneau', 'créneaux'));
  if (labels.length > 2) parts.push(plural(nTeams, 'équipe', 'équipes'));
  else if (labels.length) parts.push(labels.join(', '));
  return parts.join(' · ') || (h.usage ?? KIND_LABEL[h.kind]);
}

// --- Vérifier une séance qu'on enregistre (s3) ----------------------------------------------------------------------
export interface Proposal {
  hallId: string; teamId: string; eventId?: string | null;
  /** Premier jour (une série : sa première date) ; heures « 18:00 ». */
  day: string; start: string; end: string;
  weekdays?: number[] | null; until?: string | null;
}
export interface Suggest { label: string; day: string; start: string; end: string; hallId: string; why?: string }
export interface Check {
  clash: { day: string; with: string; from: Date; to: Date; minutes: number; days: number } | null;
  here: Suggest[]; elsewhere: Suggest[];
}
/** Les jours d'une séance (une série : 8 semaines au plus, à partir d'aujourd'hui). */
export function proposalDays(p: Proposal, today: string): string[] {
  if (!p.weekdays?.length) return [p.day];
  const out: string[] = [];
  const end = p.until && p.until < addDays(today, 55) ? p.until : addDays(today, 55);
  for (let d = p.day > today ? p.day : today; d <= end; d = addDays(d, 1)) if (p.weekdays.includes(isoDay(d))) out.push(d);
  return out;
}
/** Ce qui chevauche la séance dans sa salle (le premier jour, sur combien de jours), et ce qui est libre ce jour-là :
 *  dans cette salle (un créneau libéré, un créneau de la mairie sans équipe), dans une autre salle (rien à cette heure,
 *  dans un créneau de la mairie si la salle en a). Jamais bloquant : l'écran le dit, le coach choisit. */
export function checkProposal(b: Busy, p: Proposal): Check {
  const none: Check = { clash: null, here: [], elsewhere: [] };
  if (!p.hallId || !p.start || !p.end || p.end <= p.start) return none;
  const live = (hall: string, day: string) => b.occ.filter(o => o.hall_id === hall && o.day === day && !o.cancelled && o.event_id !== p.eventId);
  let first: Check['clash'] = null, n = 0;
  for (const day of proposalDays(p, b.today)) {
    const s = at(day, p.start), e = at(day, p.end);
    const hit = live(p.hallId, day).find(o => overlap(s, e, new Date(o.starts), new Date(o.ends)));
    if (!hit) continue;
    n++;
    if (!first) {
      const os = new Date(hit.starts), oe = new Date(hit.ends);
      const from = s > os ? s : os, to = e < oe ? e : oe;
      first = { day, with: occTitle(b, hit), from, to, minutes: minutes(from, to), days: 0 };
    }
  }
  if (!first) return none;
  first.days = n;
  const day = first.day;
  // Ici : les créneaux libérés de ce jour, les créneaux de la mairie sans équipe.
  const here: Suggest[] = [];
  const cancelled = b.occ.filter(o => o.hall_id === p.hallId && o.day === day && o.cancelled && o.kind !== 'match' && o.team_id !== p.teamId);
  for (const o of cancelled) {
    const s = new Date(o.starts), e = new Date(o.ends);
    if (live(p.hallId, day).some(x => overlap(s, e, new Date(x.starts), new Date(x.ends)))) continue;
    here.push({ label: `${dayName(day)} ${range(s, e)}`, day, start: hhmm(s), end: hhmm(e), hallId: p.hallId, why: `${shortTeam(teamName(b, o.team_id), b.teams.map(t => t.name))} annulé` });
  }
  for (const sl of b.slots.filter(x => x.hall_id === p.hallId && !x.team_id && x.weekday === isoDay(day))) {
    const s = at(day, sl.starts), e = at(day, sl.ends);
    if (live(p.hallId, day).some(x => overlap(s, e, new Date(x.starts), new Date(x.ends)))) continue;
    here.push({ label: `${dayName(day)} ${range(s, e)}`, day, start: sl.starts, end: sl.ends, hallId: p.hallId, why: 'créneau du club' });
  }
  // Ailleurs : une autre salle de sport, rien à cette heure-là (dans un créneau de la mairie si elle en a).
  const elsewhere: Suggest[] = [];
  const s0 = at(day, p.start), e0 = at(day, p.end);
  for (const h of b.halls.filter(x => x.id !== p.hallId && x.kind !== 'reception')) {
    if (live(h.id, day).some(o => overlap(s0, e0, new Date(o.starts), new Date(o.ends)))) continue;
    const slots = b.slots.filter(x => x.hall_id === h.id);
    if (slots.length) {
      const sl = slots.find(x => x.weekday === isoDay(day) && x.starts <= p.start && x.ends >= p.end
        && (!x.team_id || !b.occ.some(o => o.hall_id === h.id && o.day === day && !o.cancelled && o.team_id === x.team_id)));
      if (!sl) continue;
      elsewhere.push({ label: `${h.name} : ${dayName(day)} ${range(sl.starts, sl.ends)}`, day, start: p.start, end: p.end, hallId: h.id });
    } else {
      elsewhere.push({ label: `${h.name} : ${dayName(day)} ${range(p.start, p.end)}`, day, start: p.start, end: p.end, hallId: h.id });
    }
    if (elsewhere.length >= 2) break;
  }
  return { clash: first, here: here.slice(0, 2), elsewhere };
}
/** « Chevauche U15 filles mercredi 4, 18h00–18h30 » (et sur combien de jours). */
export function clashText(c: NonNullable<Check['clash']>): string {
  return `Chevauche ${c.with} ${dayName(c.day)} ${num(parseDay(c.day).getDate())}, ${range(c.from, c.to)}${c.days > 1 ? ` (et ${c.days - 1} ${c.days > 2 ? 'autres jours' : 'autre jour'})` : ''}`;
}
/** « Libre dans cette salle : mercredi 14h00–15h30 (U13 annulé). Salle Bellevue : mercredi 18h00–20h00. » */
export function freeText(c: Check): string {
  const parts: string[] = [];
  if (c.here.length) parts.push(`Libre dans cette salle : ${c.here.map(x => `${x.label}${x.why ? ` (${x.why})` : ''}`).join(', ')}.`);
  for (const x of c.elsewhere) parts.push(`${x.label}.`);
  return parts.length ? parts.join(' ') : 'Rien de libre à cette heure-là dans les autres salles.';
}

// --- Une demande de salle de réception -------------------------------------------------------------------------------
/** Ce qui se passe ce jour-là autour de la demande, dans la salle et dans celle où elle se trouve :
 *  « Libre ce jour-là. Juste avant : SF2 – BC Tavernel jusqu’à 17h30. » */
export function requestContext(b: Busy, req: { id?: string; hall_id: string; day: string; starts: string; ends: string }): string {
  const hall = b.halls.find(h => h.id === req.hall_id);
  const halls = new Set([req.hall_id, ...(hall?.inside_id ? [hall.inside_id] : [])]);
  const s = at(req.day, req.starts), e = at(req.day, req.ends);
  const items = [
    ...b.occ.filter(o => halls.has(o.hall_id) && o.day === req.day && !o.cancelled).map(o => ({ title: occTitle(b, o), s: new Date(o.starts), e: new Date(o.ends), same: o.hall_id === req.hall_id })),
    ...b.bookings.filter(x => x.hall_id === req.hall_id && x.day === req.day && x.id !== req.id)
      .map(x => ({ title: purposeLabel(x.purpose, x.purpose_text), s: at(x.day, x.starts), e: at(x.day, x.ends), same: true })),
  ];
  const taken = items.filter(x => x.same && overlap(s, e, x.s, x.e));
  if (taken.length) return `Déjà pris : ${taken.map(x => `${x.title}, ${range(x.s, x.e)}`).join(' ; ')}.`;
  const before = items.filter(x => x.e <= s && s.getTime() - x.e.getTime() <= 3 * 3600_000).sort((a, c) => c.e.getTime() - a.e.getTime())[0];
  const after = items.filter(x => x.s >= e && x.s.getTime() - e.getTime() <= 3 * 3600_000).sort((a, c) => a.s.getTime() - c.s.getTime())[0];
  const busyAround = items.filter(x => overlap(s, e, x.s, x.e) && !x.same)[0];
  return ['Libre ce jour-là.', before ? `Juste avant : ${before.title} jusqu’à ${hm(before.e)}.` : '',
    after ? `Juste après : ${after.title} à ${hm(after.s)}.` : '', busyAround ? `Au gymnase à cette heure : ${busyAround.title}.` : '']
    .filter(Boolean).join(' ');
}

// --- Le message à la mairie (s6) : vouvoiement, jamais envoyé par l'app ----------------------------------------------
/** « la salle polyvalente », « le club-house », « l’espace Bellevue ». */
export function withArticle(name: string): string {
  const n = lowerFirst(name.trim());
  if (/^(salle|maison|halle|médiathèque|mairie)\b/i.test(n)) return `la ${n}`;
  if (/^[aeiouyhàâéèêëîïôûü]/i.test(n)) return `l’${n}`;
  return `le ${n}`;
}
/** « du Basket Club de Rivebelle », « de l’AS Valmorin », « de Pau Basket ». */
export function clubOf(club: string): string {
  const c = club.trim();
  if (/^(basket|club|cercle|stade|racing|foyer|bc|cb|sporting)\b/i.test(c)) return `du ${c}`;
  if (/^(as|us|es|asc|union|association|entente|étoile|elan|élan|avenir|alliance)\b/i.test(c)) return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `de l’${c}` : `de la ${c}`;
  return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `d’${c}` : `de ${c}`;
}
/** « Le Basket Club de Rivebelle », « L’AS Valmorin », « Pau Basket ». */
export function clubSubject(club: string): string {
  const d = clubOf(club);
  return d.startsWith('du ') ? `Le ${d.slice(3)}` : d.startsWith('de la ') ? `La ${d.slice(6)}` : d.startsWith('de l’') ? `L’${d.slice(5)}` : club.trim();
}
/** « Nathalie D. » d'après « Nathalie Durand ». */
export const shortName = (full: string | null | undefined) => {
  const w = (full ?? '').trim().split(/\s+/).filter(Boolean);
  return w.length > 1 ? `${w[0]} ${w[w.length - 1]!.charAt(0).toUpperCase()}.` : (w[0] ?? '');
};
const purposePhrase = (p: Purpose, text: string | null) =>
  p === 'pot' ? 'pour un pot après un match' : p === 'parents' ? 'pour une réunion des parents' : p === 'anniversaire' ? 'pour l’anniversaire du club'
    : text?.trim() ? `pour ${lowerFirst(text.trim())}` : 'pour un moment du club';
export interface MairieMail { to: string; toLabel: string; subject: string; body: string; mailto: string }
export function mairieMail(req: { day: string; starts: string; ends: string; purpose: Purpose; purpose_text: string | null; people: number | null },
  hallName: string, club: string, to: { name?: string | null; email?: string | null }, signer: { name?: string | null; title?: string | null }): MairieMail {
  const day = longDay(req.day);
  const subject = `Réservation de ${withArticle(hallName)} le ${day}`;
  const who = shortName(signer.name);
  const sign = who ? `${who}, ${signer.title?.trim() ? signer.title.trim() : 'pour le bureau'} ${clubOf(club)}` : `Le bureau ${clubOf(club)}`;
  const body = [
    'Madame, Monsieur,', '',
    `${clubSubject(club)} souhaite réserver ${withArticle(hallName)} le ${day} de ${hm(req.starts)} à ${hm(req.ends)}, ${purposePhrase(req.purpose, req.purpose_text)}${req.people ? ` (environ ${req.people} personnes)` : ''}.`,
    '', 'Pouvez-vous nous confirmer sa disponibilité ? Nous restons à votre disposition pour les conditions d’usage.', '',
    'Bien cordialement,', sign,
  ].join('\n');
  const email = to.email?.trim() ?? '';
  const toLabel = `${to.name?.trim() || 'La mairie'}${email ? ` (${email})` : ' (adresse à compléter)'}`;
  const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return { to: email, toLabel, subject, body, mailto };
}
